import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {Box} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {useGetVehicleQuery, useGetVehicleV2Query} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {
  getCustomTenantCatalogue,
  getVehicleMakeModels,
  selectCarAudit,
  useThunkDispatch,
  useVehicleTitle,
} from '@dms/teas';

import {useDebouncedCallback, useRequiredParams} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {VehicleDetailContent} from './components/VehicleDetailContent';

interface VehicleDetailsProps {
  maximization?: EagGridMaximization;
}

const VehicleDetail = (props: VehicleDetailsProps) => {
  const {id} = useRequiredParams();

  const {data: vehicle} = useGetVehicleV2Query({vehicleId: id});
  const vehicleTitle = useVehicleTitle(vehicle);

  return (
    <>
      <Helmet title={i18n.t('page.vehicle.title', {title: vehicleTitle})} />
      <VehicleDetailContent vehicleId={id} maximization={props.maximization} />
    </>
  );
};

export const VehicleDetails = (props: VehicleDetailsProps) => {
  const dispatch = useThunkDispatch();

  const {id: vehicleId} = useRequiredParams();
  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const [isAuditLoading, setIsAuditLoading] = useState(false);

  const {loading} = useSelector(selectCarAudit);

  // Sometimes the loading states changes from false to true (probably because request was not fired yet and initial value is false, not true), which results in double loading
  // so we introduced a debounce to mitigate this random changes
  const checkIfAuditIsLoading = (states: boolean[]) => {
    const isLoading = states.some(isTrue);
    if (!isLoading) {
      setIsAuditLoading(false);
    }
  };

  const debouncedCheckIfAuditIsLoading = useDebouncedCallback(checkIfAuditIsLoading, 150);

  useEffect(
    function checkAuditLoadingState() {
      const auditLoadingStates = [loading['vehicleCatalogue/getCustomTenantCatalogue']];
      debouncedCheckIfAuditIsLoading(auditLoadingStates);
    },
    [debouncedCheckIfAuditIsLoading, loading]
  );

  useEffect(
    function fetchMakeModelsAndCatalogue() {
      if (isNotNil(vehicleId) && isNotNil(vehicle) && !isAuditLoading) {
        Promise.all([
          dispatch(
            getVehicleMakeModels.action({
              vehicleType: vehicle.type!,
              make: vehicle.make,
            })
          ),
          dispatch(getCustomTenantCatalogue.action({})),
        ]);
      }
    },
    [vehicle, vehicleId, dispatch, isAuditLoading]
  );

  return (
    <Box flex={1} minWidth={0}>
      <DataStatus isLoading={isAuditLoading} minHeight={100}>
        <VehicleDetail maximization={props.maximization} />
      </DataStatus>
    </Box>
  );
};
